import {FC, useEffect, useState} from 'react'
import {AzPageHeader} from '../../../components/AzPageHeader'
import {useIntl} from 'react-intl'
import {useParams, useNavigate} from 'react-router-dom'
import {useQuery, useMutation, useQueryClient} from 'react-query'
import {getOneByIdbylang, patchOne} from '../../../services/common_requests'
import {KTCard, KTCardBody, KTSVG} from '../../../../_dashboard/helpers'
import dataAxios from '../../../modules/auth/core/dataAxios'
import {toast} from 'react-toastify'
import {Dropdown} from 'react-bootstrap'
import React from 'react'

const styles = {
  cardBodyClass: 'py-4 bg-light-gray',
  headerClass: 'bg-light mb-2',
  contentGapClass: 'gap-2',
  headerStyle: {
    borderRadius: '0.7rem',
  },
}

interface PriceDetails {
  basePrice?: {
    priceTTC?: number
  }
  extras?: Array<{
    id: string
    name: string
    totalTTC: number
  }>
  subTotalTTC?: number
  groupDiscount?: {
    amountTTC?: number
    discountValue?: number
    discountType?: string
    displayAsDiscount?: boolean
  }
  promotion?: {
    code?: string
    amountTTC?: number
    name?: string
  }
  totalDiscountsTTC?: number
  finalTotalTTC?: number
}

const normalizeReservationStatusCode = (rawCode?: string) => {
  const normalized = (rawCode || '').trim().toLowerCase()
  if (!normalized) return 'pending'
  return normalized.startsWith('status_') ? normalized.replace(/^status_/, '') : normalized
}

// Custom toggle component to avoid 'dropdown-toggle' class
const StatusDropdownToggle = React.forwardRef<HTMLSpanElement, any>(
  ({children, onClick, disabled}, ref) => (
    <span
      ref={ref}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        opacity: disabled ? 0.75 : 1,
      }}
      aria-disabled={disabled ? true : undefined}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault()
          return
        }
        e.preventDefault()
        onClick(e)
      }}
    >
      {children}
    </span>
  )
)

const TourReservationDetailsPage: FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {id} = useParams<{id: string}>()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {data: reservation, isLoading} = useQuery(
    ['reservation', id],
    () => getOneByIdbylang(`/tour-reservations/${id}`),
    {
      enabled: !!id,
    }
  )

  const updateStatusMutation = useMutation((newStatus: string) =>
    patchOne({statusCode: newStatus}, `/tour-reservations/${id}/status`)
  )

  const handleStatusChange = async (newStatus: string) => {
    const reservationId = id ? String(id) : ''
    try {
      setIsSubmitting(true)
      await updateStatusMutation.mutateAsync(newStatus)

      // Recharger la fiche comme au premier rendu (même pipeline que useQuery).
      if (reservationId) {
        try {
          const fresh = await getOneByIdbylang(`/tour-reservations/${reservationId}`)
          if (fresh) {
            queryClient.setQueryData(['reservation', id], fresh)
          }
        } catch {
          queryClient.invalidateQueries(['reservation', id])
        }
      }

      const normalized = normalizeReservationStatusCode(newStatus)
      queryClient.setQueriesData(['tourReservations'], (oldData: any) => {
        if (!oldData) return oldData
        const updateItem = (item: any) =>
          item && String(item._id) === reservationId
            ? {
                ...item,
                status: {
                  ...(typeof item.status === 'object' && item.status ? item.status : {}),
                  code: normalized,
                },
              }
            : item

        if (Array.isArray(oldData)) {
          return oldData.map(updateItem)
        }
        if (Array.isArray(oldData.data)) {
          return {...oldData, data: oldData.data.map(updateItem)}
        }
        return oldData
      })

      queryClient.invalidateQueries(['tourReservations'])
      toast.success(intl.formatMessage({id: 'RESERVATION.STATUS_UPDATED'}))
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.payload?.message ||
        error?.message;
      const fallback = intl.formatMessage({id: 'ERROR.UPDATING_STATUS'})
      toast.error(serverMessage ? String(serverMessage) : fallback)
    } finally {
      setIsSubmitting(false)
    }
  }

  const statusUpdatePending = isSubmitting || updateStatusMutation.isLoading

  const statusColor = (code?: string) => {
    switch (normalizeReservationStatusCode(code)) {
      case 'confirmed':
      case 'completed':
        return 'success'
      case 'paid':
        return 'info'
      case 'pending':
      case 'payment_pending':
        return 'warning'
      case 'canceled':
      case 'failed':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const statusIcon = (code?: string) => {
    switch (normalizeReservationStatusCode(code)) {
      case 'confirmed':
      case 'completed':
        return '/media/icons/duotune/general/gen048.svg'
      case 'paid':
        return '/media/icons/duotune/finance/fin008.svg'
      case 'pending':
      case 'payment_pending':
        return '/media/icons/duotune/general/gen044.svg'
      case 'canceled':
      case 'failed':
        return '/media/icons/duotune/general/gen040.svg'
      default:
        return '/media/icons/duotune/general/gen005.svg'
    }
  }

  const currentStatusCode = normalizeReservationStatusCode(reservation?.status?.code)

  const isVoucherEligibleStatus = ['confirmed', 'paid', 'completed'].includes(currentStatusCode)

  const handleDownloadVoucher = async () => {
    if (!reservation?._id) return
    const apiBase = process.env.REACT_APP_API_URL || ''
    const imageBase = process.env.REACT_APP_API_URL_IMAGE || apiBase

    let href: string | null = null
    let isDirectLink = false

    if (reservation.downloadVoucherLink) {
      href = `${imageBase}${reservation.downloadVoucherLink}`
      isDirectLink = true
    } else if (reservation.voucherUrl) {
      href = reservation.voucherUrl
      isDirectLink = true
    } else {
      href = `${apiBase}/tour-reservations/${reservation._id}/voucher`
      isDirectLink = false
    }

    if (!href) return

    if (isDirectLink) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }

    try {
      const resp = await dataAxios.get(href, {responseType: 'blob'})
      const blob = resp.data as Blob
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `voucher-${reservation.orderNumber || reservation._id}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (e: any) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error(intl.formatMessage({id: 'ERROR.UNAUTHORIZED', defaultMessage: 'Unauthorized'}))
      } else {
        toast.error(
          e?.message ||
            intl.formatMessage({id: 'ERROR.DOWNLOAD_FAILED', defaultMessage: 'Download failed'})
        )
      }
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (!reservation) {
    return (
      <div className='alert alert-danger' role='alert'>
        {intl.formatMessage({id: 'ERROR.RESERVATION_NOT_FOUND'})}
      </div>
    )
  }

  // Safely parse priceCalculationDetails with fallback
  let priceDetails: PriceDetails = {}
  try {
    if (typeof reservation.priceCalculationDetails === 'string') {
      priceDetails = JSON.parse(reservation.priceCalculationDetails)
    } else if (
      typeof reservation.priceCalculationDetails === 'object' &&
      reservation.priceCalculationDetails !== null
    ) {
      priceDetails = reservation.priceCalculationDetails
    } else {
      priceDetails = {}
    }
  } catch (error) {
    console.error('Error parsing price details:', error)
    priceDetails = {}
  }

  return (
    <>
      <AzPageHeader title={intl.formatMessage({
          id: 'TOUR_RESERVATIONS.DETAILS',
          defaultMessage: 'Reservation Details',
        })} />

      <div 
        className='d-flex justify-content-between align-items-center p-4 bg-light mb-2 flex-wrap flex-md-nowrap mx-10'
        style={styles.headerStyle}
      >
        <div className='d-flex align-items-center gap-3 mb-3 mb-md-0'>
          <KTSVG
            path='/media/icons/duotune/general/gen005.svg'
            className='svg-icon-2x text-primary'
          />
          <div>
            <h3 className='mb-1'>Order #{reservation.orderNumber}</h3>
            <div className='text-muted'>{new Date(reservation.createdAt).toLocaleDateString()}</div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3'>
          {isVoucherEligibleStatus && (
            <button
              type='button'
              onClick={handleDownloadVoucher}
              className='btn btn-primary d-flex align-items-center gap-2'
            >
              <KTSVG path='/media/icons/duotune/arrows/arr016.svg' className='svg-icon-1' />
              {intl.formatMessage({
                id: 'TOUR_RESERVATIONS.DOWNLOAD_VOUCHER',
                defaultMessage: 'Download voucher',
              })}
            </button>
          )}
          <Dropdown align='end'>
            <Dropdown.Toggle as={StatusDropdownToggle} id='status-dropdown' disabled={statusUpdatePending}>
              <span
                className={`badge badge-lg badge-light-${statusColor(reservation.status?.code)}`}
                style={{
                  fontSize: '1.1rem',
                  padding: '0.75em 1.5em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  fontWeight: 600,
                }}
              >
                {statusUpdatePending ? (
                  <span
                    className='spinner-border spinner-border-sm text-primary me-2'
                    role='status'
                    aria-hidden='true'
                  />
                ) : (
                  <KTSVG path={statusIcon(reservation.status?.code)} className='svg-icon-2 me-2' />
                )}
                {statusUpdatePending
                  ? intl.formatMessage({
                      id: 'RESERVATION.STATUS_UPDATING',
                      defaultMessage: 'Updating…',
                    })
                  : intl.formatMessage({id: `STATUS.${currentStatusCode.toUpperCase()}`})}
              </span>
              <KTSVG
                path='/media/icons/duotune/arrows/arr072.svg'
                className={`svg-icon-2 ms-2 text-${statusColor(reservation.status?.code)}`}
              />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                disabled={statusUpdatePending}
                onClick={() => handleStatusChange('confirmed')}
                active={currentStatusCode === 'confirmed'}
              >
                <KTSVG path={statusIcon('confirmed')} className='svg-icon-1 me-2 text-success' />
                {intl.formatMessage({id: 'STATUS.CONFIRMED', defaultMessage: 'Confirmed'})}
              </Dropdown.Item>
              <Dropdown.Item
                disabled={statusUpdatePending}
                onClick={() => handleStatusChange('canceled')}
                active={currentStatusCode === 'canceled'}
              >
                <KTSVG path={statusIcon('canceled')} className='svg-icon-1 me-2 text-danger' />
                {intl.formatMessage({id: 'STATUS.CANCELED', defaultMessage: 'Canceled'})}
              </Dropdown.Item>
              <Dropdown.Item
                disabled={statusUpdatePending}
                onClick={() => handleStatusChange('paid')}
                active={currentStatusCode === 'paid'}
              >
                <KTSVG path={statusIcon('paid')} className='svg-icon-1 me-2 text-info' />
                {intl.formatMessage({id: 'STATUS.PAID', defaultMessage: 'Paid'})}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <KTCard>
        <KTCardBody className={styles.cardBodyClass}>
          <div className={`d-flex flex-column ${styles.contentGapClass}`}>
            {/* Tour Details Section as a single card */}
            <div className='m-2 mb-1'>
              <div className='card card-flush py-4'>
                <div className='card-header'>
                  {/* <KTSVG
                    path='/media/icons/duotune/maps/map002.svg'
                    className='svg-icon-2 me-2 text-primary'
                  /> */}
                  <h3 className='card-title mb-0'>
                    {intl.formatMessage({id: 'TOUR.DETAILS', defaultMessage: 'Tour Details'})}
                  </h3>
                </div>
                <div className='card-body pt-0'>
                  <div className='d-flex flex-wrap gap-4 align-items-center'>
                    {/* Banner Image */}
                    {/* {reservation.tour?.banner?.url && (
                      <img
                        src={reservation.tour.banner.url}
                        alt='Tour Banner'
                        style={{
                          width: 60,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 6,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                        className='me-3'
                      />
                    )} */}
                    {/* Tour Name */}
                    {reservation.tour?._id && reservation.tour?.title && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen022.svg'
                          className='svg-icon-1 text-info'
                        />
                        <a
                          href={`/apps/tours/${reservation.tour._id}`}
                          className='fw-bold text-hover-primary text-primary'
                          style={{textDecoration: 'underline', cursor: 'pointer'}}
                        >
                          {reservation.tour.title}
                        </a>
                      </div>
                    )}
                    {/* Category */}
                    {reservation.tour?.category?.title && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen040.svg'
                          className='svg-icon-1 text-warning'
                        />
                        <span>{reservation.tour.category.title}</span>
                      </div>
                    )}
                    {/* City */}
                    {reservation.tour?.city?.name && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/maps/map001.svg'
                          className='svg-icon-1 text-success'
                        />
                        <span>{reservation.tour.city.name}</span>
                      </div>
                    )}
                    {/* Duration */}
                    {reservation.tour?.duration != null && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen014.svg'
                          className='svg-icon-1 text-primary'
                        />
                        <span>
                          {reservation.tour.duration}{' '}
                          {intl.formatMessage({
                            id:
                              reservation.tour.durationUnit === 'days'
                                ? 'TOUR.PLACEHOLDERS.DURATION.DAYS'
                                : 'TOUR.PLACEHOLDERS.DURATION.HOURS',
                          })}
                        </span>
                      </div>
                    )}
                    {/* Min/Max People */}
                    {(reservation.tour?.tourMinPeople || reservation.tour?.tourMaxPeople) && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/communication/com013.svg'
                          className='svg-icon-1 text-info'
                        />
                        <span>
                          {reservation.tour.tourMinPeople || '-'} -{' '}
                          {reservation.tour.tourMaxPeople || '-'}{' '}
                          {intl.formatMessage({
                            id: 'TOUR.FIELDS.CAPACITY',
                            defaultMessage: 'People',
                          })}
                        </span>
                      </div>
                    )}
                    {/* Availability Dates */}
                    {(reservation.tour?.disponibilite_from ||
                      reservation.tour?.disponibilite_to) && (
                      <div className='d-flex align-items-center gap-2'>
                        <KTSVG
                          path='/media/icons/duotune/general/gen018.svg'
                          className='svg-icon-1 text-danger'
                        />
                        <span>
                          {reservation.tour.disponibilite_from
                            ? new Date(reservation.tour.disponibilite_from).toLocaleDateString()
                            : '-'}{' '}
                          →{' '}
                          {reservation.tour.disponibilite_to
                            ? new Date(reservation.tour.disponibilite_to).toLocaleDateString()
                            : '-'}
                        </span>
                      </div>
                    )}
                    {/* Video Link */}
                    {reservation.tour?.youtubeVideo && (
                      <div className='d-flex align-items-center gap-2'>
                        <a
                          href={reservation.tour.youtubeVideo}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='d-flex align-items-center gap-2 text-danger text-hover-primary'
                        >
                          <KTSVG
                            path='/media/icons/duotune/general/gen057.svg'
                            className='svg-icon-1 text-danger'
                          />
                          {intl.formatMessage({
                            id: 'TOUR.FIELDS.VIDEO',
                            defaultMessage: 'Video',
                          })}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* Main Content */}
            <div className='row g-2 m-2'>
              {/* Reservation Details and Price Details combined */}
              <div className='col-12 mb-2'>
                <div className='card card-flush py-4'>
                  <div className='card-body pt-0'>
                    <div className='row g-2'>
                      {/* Left Column - Reservation Details */}
                      <div className='col-lg-6 custom-border-lg-end pe-3'>
                        <h3 className='card-title mb-3'>
                          {intl.formatMessage({
                            id: 'TOUR_RESERVATIONS.DETAILS',
                            defaultMessage: 'Reservation Details',
                          })}
                        </h3>
                        <div className='table-responsive'>
                          <table className='table align-middle fs-6 gy-5'>
                            <tbody>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen014.svg'
                                    className='svg-icon-1 me-2 text-info'
                                  />
                                  {intl.formatMessage({
                                    id: 'TOUR_RESERVATIONS.TOUR_DATE',
                                    defaultMessage: 'Tour Date',
                                  })}
                                </td>
                                <td>{new Date(reservation.tourDate).toLocaleDateString()}</td>
                              </tr>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/communication/com013.svg'
                                    className='svg-icon-1 me-2 text-primary'
                                  />
                                  {intl.formatMessage({
                                    id: 'TOUR_RESERVATIONS.PARTICIPANTS',
                                    defaultMessage: 'Participants',
                                  })}
                                </td>
                                <td>
                                  {intl.formatMessage(
                                    {
                                      id: 'TOUR_RESERVATIONS.PARTICIPANTS_COUNT',
                                      defaultMessage: '{adults} Adults, {children} Children',
                                    },
                                    {
                                      adults: reservation.adultsCount,
                                      children: reservation.childrenCount,
                                    }
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td className='text-gray-800'>
                                  <KTSVG
                                    path='/media/icons/duotune/finance/fin010.svg'
                                    className='svg-icon-1 me-2 text-warning'
                                  />
                                  {intl.formatMessage({
                                    id: 'TOUR_RESERVATIONS.PAYMENT_METHOD',
                                    defaultMessage: 'Payment Method',
                                  })}
                                </td>
                                <td>
                                  {reservation.paymentMethod?.description ||
                                    reservation.paymentMethod?.code ||
                                    reservation.paymentMethod ||
                                    '-'}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Right Column - Price Details */}
                      <div className='col-lg-6 ps-3'>
                        <h3 className='card-title mb-3'>
                          {intl.formatMessage({
                            id: 'TOUR_RESERVATIONS.PRICE_DETAILS',
                            defaultMessage: 'Price Details',
                          })}
                        </h3>
                        <div className='d-flex flex-column gap-6 mt-8'>
                          <div className='d-flex justify-content-between'>
                            <span className='text-gray-600'>
                              <KTSVG
                                path='/media/icons/duotune/finance/fin001.svg'
                                className='svg-icon-1 me-2 text-info'
                              />
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.BASE_PRICE',
                                defaultMessage: 'Base Price',
                              })}
                            </span>
                            <span className='text-gray-800 fw-bold'>
                              {priceDetails.basePrice?.priceTTC?.toFixed(2) || '0.00'}{' '}
                              {reservation.currencySymbol}
                            </span>
                          </div>
                          {priceDetails.extras?.map((extra: any) => (
                            <div key={extra.id} className='d-flex justify-content-between'>
                              <span className='text-gray-600'>
                                <KTSVG
                                  path='/media/icons/duotune/finance/fin002.svg'
                                  className='svg-icon-1 me-2 text-success'
                                />
                                {extra.name}
                              </span>
                              <span className='text-gray-800 fw-bold'>
                                {extra.totalTTC.toFixed(2)} {reservation.currencySymbol}
                              </span>
                            </div>
                          ))}
                          {priceDetails.subTotalTTC != null && (
                            <div className='d-flex justify-content-between'>
                              <span className='text-gray-600'>
                                {intl.formatMessage({
                                  id: 'TOUR_RESERVATIONS.SUBTOTAL',
                                  defaultMessage: 'Subtotal',
                                })}
                              </span>
                              <span className='text-gray-800 fw-bold'>
                                {priceDetails.subTotalTTC.toFixed(2)} {reservation.currencySymbol}
                              </span>
                            </div>
                          )}
                          {priceDetails.groupDiscount?.displayAsDiscount === true &&
                          priceDetails.groupDiscount?.amountTTC ? (
                            <div className='d-flex justify-content-between'>
                              <span className='text-gray-600'>
                                {intl.formatMessage({
                                  id: 'TOUR_RESERVATIONS.GROUP_DISCOUNT',
                                  defaultMessage: 'Group discount',
                                })}
                              </span>
                              <span className='text-success fw-bold'>
                                −{priceDetails.groupDiscount.amountTTC.toFixed(2)}{' '}
                                {reservation.currencySymbol}
                              </span>
                            </div>
                          ) : null}
                          {(reservation.promotionCode || priceDetails.promotion?.amountTTC) && (
                            <div className='d-flex justify-content-between'>
                              <span className='text-gray-600'>
                                {intl.formatMessage({
                                  id: 'TOUR_RESERVATIONS.PROMO_CODE',
                                  defaultMessage: 'Promo code',
                                })}
                                {reservation.promotionCode ? `: ${reservation.promotionCode}` : ''}
                              </span>
                              {priceDetails.promotion?.amountTTC ? (
                                <span className='text-success fw-bold'>
                                  −{priceDetails.promotion.amountTTC.toFixed(2)}{' '}
                                  {reservation.currencySymbol}
                                </span>
                              ) : null}
                            </div>
                          )}
                          {priceDetails.totalDiscountsTTC ? (
                            <div className='d-flex justify-content-between'>
                              <span className='text-gray-600'>
                                {intl.formatMessage({
                                  id: 'TOUR_RESERVATIONS.TOTAL_DISCOUNTS',
                                  defaultMessage: 'Total discounts',
                                })}
                              </span>
                              <span className='text-success fw-bold'>
                                −{priceDetails.totalDiscountsTTC.toFixed(2)}{' '}
                                {reservation.currencySymbol}
                              </span>
                            </div>
                          ) : null}
                          <div className='d-flex justify-content-between'>
                            <span className='text-gray-800 fw-bold'>
                              <KTSVG
                                path='/media/icons/duotune/finance/fin003.svg'
                                className='svg-icon-1 me-2 text-primary'
                              />
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.TOTAL',
                                defaultMessage: 'Total',
                              })}
                            </span>
                            <span className='text-gray-800 fw-bold'>
                              {reservation.totalPrice.toFixed(2)} {reservation.currencySymbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Participants List */}
              <div className='col-12'>
                <div className='card card-flush py-4'>
                  <div className='card-header'>
                    <h3 className='card-title'>
                      {intl.formatMessage({
                        id: 'TOUR_RESERVATIONS.PARTICIPANTS_LIST',
                        defaultMessage: 'Participants List',
                      })}
                    </h3>
                  </div>
                  <div className='card-body pt-0'>
                    <div className='table-responsive'>
                      <table className='table align-middle table-row-dashed fs-6 gy-5'>
                        <thead>
                          <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                            <th>
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.NAME',
                                defaultMessage: 'Name',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.EMAIL',
                                defaultMessage: 'Email',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.PHONE',
                                defaultMessage: 'Phone',
                              })}
                            </th>
                            <th>
                              {intl.formatMessage({
                                id: 'TOUR_RESERVATIONS.TYPE',
                                defaultMessage: 'Type',
                              })}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservation.participants.map((participant: any) => (
                            <tr key={participant._id}>
                              <td>
                                <KTSVG
                                  path='/media/icons/duotune/communication/com006.svg'
                                  className='svg-icon-1 me-2 text-primary'
                                />
                                {`${participant.firstName} ${participant.lastName}`}
                              </td>
                              <td>{participant.email}</td>
                              <td>{participant.phone}</td>
                              <td>
                                <span
                                  className={`badge badge-light-${
                                    participant.isAdult ? 'primary' : 'warning'
                                  }`}
                                  style={{fontSize: '0.95em'}}
                                >
                                  {participant.isAdult
                                    ? intl.formatMessage({
                                        id: 'TOUR_RESERVATIONS.ADULT',
                                        defaultMessage: 'Adult',
                                      })
                                    : intl.formatMessage({
                                        id: 'TOUR_RESERVATIONS.CHILD',
                                        defaultMessage: 'Child',
                                      })}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export default TourReservationDetailsPage
